import {
    CardItem,
    CollectionEvent,
    ConfigurationContext,
    createCardClickedEvent,
    isPublicationCardItem,
    Product,
    toLinkWithHint,
    useFeature,
    useToggleState,
} from '@news-mono/web-common'
import { breakpoints, useWindowWidth } from '@news-mono/component-library'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import React, {
    useContext,
    useRef,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react'
import {
    StyledCTAButton,
    StyledCardImage,
    StyledCardImageContainer,
    StyledContentCard,
    StyledLinkWrapper,
    StyledText,
    StyledContentCardContainer,
    StyledTimeToRead,
    StyledTimeToReadWrapper,
    StyledLiveLabel,
    StyledBreakingLabel,
    StyledSponsoredLabel,
    StyledExclusiveLabel,
    StyledLabelRow,
    StyledUpdatedLabel,
} from './ContentCard.styled'
import {
    EditorialType,
    getEditorialType,
    getSalesType,
    SalesType,
} from '../../../templates/Publication/lib/get-editorial-type'
import { useProduct } from '../../../__product/useProduct'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { useLocation } from 'react-router'

export type CardType =
    | 'large'
    | 'medium'
    | 'medium-video-hub'
    | 'small'
    | 'small-horizontal'
export type CollectionType =
    | 'six-card'
    | 'five-card'
    | 'four-card'
    | 'four-card-horizontal'
    | 'trending'
    | 'hero'

export interface CTAProps {
    title: string
    link?: string
}

export interface ContentCardProps {
    item: MaybeLoaded<CardItem>
    cardNumber: number
    cardType: CardType
    onEvent: (event: CollectionEvent) => void
    gridAreaName?: string
    displayRightSeparator?: boolean
    cta?: CTAProps
    shouldHideTeaser?: boolean
    collectionType?: CollectionType
    headlineAs?: 'h1' | 'h2' | 'div'
    enableChartbeatArticleBlock?: boolean
}

const FOUR_HORIZONTAL_IMG = '256px'

const getImageWidth = (
    cardType: CardType,
    breakpoint: 'mobile' | 'tablet' | 'desktop',
) => {
    switch (cardType) {
        case 'small':
            if (breakpoint === 'mobile') return '240px'
            if (breakpoint === 'tablet') return '540px'
            return '340px'
        case 'medium':
            if (breakpoint === 'mobile') return '740px'
            if (breakpoint === 'tablet') return '540px'
            return '340px'
        case 'medium-video-hub':
            if (breakpoint === 'mobile') return '400px'
            return '740px'
        case 'large':
            if (breakpoint === 'mobile') return '500px'
            return '740px'
        case 'small-horizontal':
            return FOUR_HORIZONTAL_IMG
    }
}

const getEditorialLabel = (
    editorialType?: EditorialType,
    salesType?: SalesType,
) => {
    switch (editorialType) {
        case EditorialType.Live:
            return <StyledLiveLabel>Live</StyledLiveLabel>
        case EditorialType.Updated:
            return <StyledUpdatedLabel>Updated</StyledUpdatedLabel>
        case EditorialType.Breaking:
            return <StyledBreakingLabel>Breaking</StyledBreakingLabel>
        case EditorialType.Exclusive:
            return <StyledExclusiveLabel>Exclusive</StyledExclusiveLabel>
        default:
            break
    }

    switch (salesType) {
        case SalesType.Sponsored:
            return <StyledSponsoredLabel>Sponsored</StyledSponsoredLabel>
        default:
            return null
    }
}

export const ContentCard: React.FC<ContentCardProps> = ({
    item,
    cardNumber,
    cardType,
    onEvent,
    gridAreaName,
    displayRightSeparator,
    cta,
    shouldHideTeaser,
    collectionType,
    headlineAs,
    enableChartbeatArticleBlock = true,
}) => {
    const product = useProduct()
    const isChartbeatImageTestingEnabled = useFeature(
        'chartbeat-image-testing',
        false,
    )
    const isCarExpert = item.loaded && item.result.source === 'CarExpert'
    const location = useLocation()
    const config = useContext(ConfigurationContext)
    const linkOutIcon = require('../../assets/link-out.svg')
    function linkClicked(
        e: React.MouseEvent<HTMLElement>,
        item: MaybeLoaded<CardItem>,
        cardNumber: number,
    ) {
        if (!item.loaded) {
            e.preventDefault()
            return
        }
        onEvent(
            createCardClickedEvent(
                onEvent,
                item.result,
                'InternalPortrait',
                'Portrait',
                cardNumber,
                'InlineRelatedCollection',
            ),
        )
    }
    const toggles = useToggleState()
    const cardRef = useRef<HTMLDivElement>(null)
    const editorialType = item.loaded
        ? getEditorialType(item.result)
        : undefined
    const salesType = item.loaded ? getSalesType(item.result) : undefined

    const imageFirst = collectionType === 'trending'
    const shouldBeSquared =
        collectionType === 'trending' && cardType !== 'small-horizontal'

    const fixedRatio = shouldBeSquared ? '4:3' : '16:9'

    const baseMobile = getImageWidth(cardType, 'mobile')
    const baseTablet = getImageWidth(cardType, 'tablet')
    const baseDesktop = getImageWidth(cardType, 'desktop')

    const imageWidths = {
        mobile: baseMobile,
        tablet: baseTablet,
        desktop: baseDesktop,
        fallbackWidth: 740,
    }

    if (item.loaded && isPublicationCardItem(item.result)) {
        const timeToRead =
            item?.result?.readTimeMinutes && item?.result?.readTimeMinutes > 0
                ? `${Math.ceil(item.result.readTimeMinutes)} min read`
                : ''
        const isCarExpertCanonicalEnabled = isFeatureEnabled(
            toFeatureState(toggles),
            'car-expert-canonical',
        )

        const currentUrl = `${config.publicUrl}${location.pathname}`

        const overrideUrl =
            isCarExpert &&
            product === 'sevennews' &&
            isCarExpertCanonicalEnabled
                ? `${item.result.canonicalUrl}?referrer=7news&referrerurl=${currentUrl}`
                : null

        return (
            <StyledLinkWrapper
                to={overrideUrl ?? toLinkWithHint(item)}
                className={
                    enableChartbeatArticleBlock &&
                    isChartbeatImageTestingEnabled
                        ? 'chartbeat-article-block'
                        : undefined
                }
                onClick={(event) => {
                    linkClicked(event, item, cardNumber)
                }}
                gridAreaName={gridAreaName}
                collectionType={collectionType}
            >
                <StyledContentCard
                    ref={cardRef}
                    cardType={cardType}
                    displayRightSeparator={displayRightSeparator}
                >
                    {imageFirst && (
                        <StyledCardImageContainer cardType={cardType}>
                            <StyledCardImage
                                item={{ loaded: true, result: item.result }}
                                imageWidths={imageWidths}
                                hasBackground
                                onEvent={onEvent}
                                mediaMode="default"
                                fixedRatio={fixedRatio}
                                disableImageLazyLoad={false}
                                displayNoScriptImage
                                isVideoPage={cardType === 'medium-video-hub'}
                            />
                        </StyledCardImageContainer>
                    )}

                    <StyledContentCardContainer
                        cardType={cardType}
                        collectionType={collectionType}
                    >
                        <StyledText
                            item={{ loaded: true, result: item.result }}
                            fontScale={1}
                            onEvent={onEvent}
                            teaserMode={
                                cardType === 'small' || shouldHideTeaser
                                    ? 'hidden'
                                    : 'visible'
                            }
                            kickerMode={'hidden'}
                            product={Product.SevenNews}
                            headlineAs={headlineAs}
                        />

                        <StyledLabelRow>
                            {getEditorialLabel(editorialType, salesType)}
                            {timeToRead && !cta && (
                                <StyledTimeToReadWrapper>
                                    <StyledTimeToRead>
                                        {timeToRead}
                                    </StyledTimeToRead>
                                    {overrideUrl && (
                                        <img
                                            src={linkOutIcon}
                                            alt={'link out icon'}
                                        />
                                    )}
                                </StyledTimeToReadWrapper>
                            )}
                        </StyledLabelRow>

                        {cta && <StyledCTAButton>{cta.title}</StyledCTAButton>}
                    </StyledContentCardContainer>

                    {!imageFirst && (
                        <StyledCardImageContainer cardType={cardType}>
                            <StyledCardImage
                                item={{ loaded: true, result: item.result }}
                                imageWidths={imageWidths}
                                hasBackground
                                onEvent={onEvent}
                                mediaMode="default"
                                fixedRatio={fixedRatio}
                                disableImageLazyLoad={false}
                                displayNoScriptImage
                                isVideoPage={cardType === 'medium-video-hub'}
                            />
                        </StyledCardImageContainer>
                    )}
                </StyledContentCard>
            </StyledLinkWrapper>
        )
    } else {
        return null
    }
}
